import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DSAlert } from '@demandstar/components/alert';
import { Status } from '@demandstar/components/constants';

import { initialInstantBidData } from 'src/store/reducers/addbid';
import { UploadedFile } from 'src/types/addbid';
import { cancelInstantBidUpload, useSubmitInstantBidFile } from 'src/query/queries/addinstantbid/submitInstantBidFile';
import {
  getAddBidBidTypes,
  getAddBidBidWriters,
  getSavedLegal,
  setAddBidDetails,
  submitAddInstantBidInfo,
} from 'src/store/actions';

import { useAuthContext } from 'src/auth/AuthProvider';
import { getJobResult, getJobStatus } from 'src/store/services';

import { FileUploadBox } from './FileUploadBox';
import { UploadProgressBar } from './UploadProgressBar';
import { DSButton } from '../../../../library/button';
import { useDSSelector } from '../../../../store/reducers';
import { queryClient } from '../../../../queryClient';
import {
  getCommodityQueryValue,
  prepareAutoSubmitPayload,
  transformInstantBidData,
} from '../../../../utils/helpers/addInstantBidHelper';
import { useParams } from 'react-router-dom';
import { useGetCommodityCodes } from 'src/query/queries/commodityCodes/getCommodityCodes';
import DuplicateInstantBid from './duplicateInstantBid';
import { ModalPopUp } from 'src/components/common/modals';
import { AppContext } from 'src/global-context/AppContext';
import { toastFn } from 'src/utils/helpers';
import { addBidToastId } from 'src/store/sagas/addbid';
import history from 'src/utils/history';
import { useGetCommodityCodesCognitiveSearch } from '../../../../query/queries/cognitiveSearch/getcommoditiyCognitiveSearchData';

export function AddInstantBid() {
  const params = useParams() as any;
  const editBidId = parseInt(params?.bidId);
  const [instantBidFile, setInstantBidFile] = useState<UploadedFile[]>([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // const [jobId, setJobId] = useState(0);
  const [status, setStatus] = useState<string>('start');
  const [progress, setProgress] = useState(0);
  const[uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [showData, setShowData] = useState(false);

  const [formData, setFormData] = useState(initialInstantBidData);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);
  const autoSubmitTriggeredRef = useRef(false);
  const bidLegalAds = useDSSelector(state => state.accountinfo.savedLegalData);

  const dispatch = useDispatch();
  const { auth } = useAuthContext();
  const memberId = auth?.memberId;
  const {
    bidwriters,
    bidtypes,
    duplicateInstantBidModel,
  } = useDSSelector(s => s.addbid);
  const { mutate: submitInstantBidFile } = useSubmitInstantBidFile({});
  const { changeInstantBidview } = useContext(AppContext);
  const [reqDocIds, setReqDocIds] = useState('');
  const { jobId: jobID } = useParams<{ jobId: string }>();
  const jobId = useMemo(() => {
    const id = Number(jobID);
    return Number.isNaN(id) ? 0 : id;
  }, [jobID]);


  const { data: commodityCodesResponse } = useGetCommodityCodes({});
  const allCommoditydata = useMemo(
    () => commodityCodesResponse?.data?.result || queryClient.getQueryData<any>(['CommodityCodes'])?.data?.result || [],
    [commodityCodesResponse],
  );


  const { data: cognitiveSearchResults } = useGetCommodityCodesCognitiveSearch(searchQuery, { enabled: searchQuery?.length > 2 });
  const relevantCommodities = useMemo(
    () => cognitiveSearchResults?.value || [],
    [cognitiveSearchResults],
  );

  const validateAndSetFile = (files: FileList | null) => {
    if (!files) return;
    const file = files[0];
    if (file.type !== 'application/pdf') {
      setFileError('Only PDF files are supported right now');
      setFileName('');
      setInstantBidFile([]);
      return;
    }
    setFileError('');
    setFileName(file.name);
    setInstantBidFile([file as any]);
  };

  const resetFile = () => {
    cancelInstantBidUpload();
    setShowData(false);
    setError('');
    setFileError('');
    setFileName('');
    setInstantBidFile([]);
    setFormData(initialInstantBidData);
    setSearchQuery('');
    setReqDocIds('');
    setStatus('');
    setProgress(0);
    setUploadProgress(0);
    setHasAutoSubmitted(false);
    // setJobId(0);
    autoSubmitTriggeredRef.current = false;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    history.replace('/buyers/bid/AddInstantBid');
  };

  const handleSubmit = () => {
    setError('');
    if (!instantBidFile.length) return;
    setStatus('uploading');
    setProgress(0);
    setUploadProgress(0);
    submitInstantBidFile(
      {
        file: instantBidFile[0],
        memberId,
        onUploadProgress: (progress: number) => {
          setUploadProgress(progress);

          const overallProgress = Math.min(
            Math.round((progress * 21) / 100),
            24,
          );

          setProgress(overallProgress);
        },
      },
      {
        onSuccess: res => {
          // setJobId(res.job_id);
          history.push(`/buyers/bid/AddInstantBid/${res.job_id}`);
          setStatus('pending');
          setError('');
          setProgress(25);
        },
        onError: (error: any) => {
          if ( error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
            return;
          }
          setError('Failed to submit file. Please try again.');
          setStatus('');
          resetFile();
        },
      },
    );
  };

  const handleAutoSubmit = useCallback((extractedFormData: any) => {
    try {
      const commoditiesToUse = cognitiveSearchResults?.value?.length
        ? cognitiveSearchResults
        : relevantCommodities.length > 0
          ? relevantCommodities
          : allCommoditydata;

      const instantBidPayload = prepareAutoSubmitPayload(
        extractedFormData,
        commoditiesToUse,
        bidtypes,
        bidwriters,
        memberId || 0,
        instantBidFile[0],
        fileName,
        reqDocIds,
        bidLegalAds,
      );


      dispatch(setAddBidDetails({ addInstantBid: instantBidPayload }));
      dispatch(submitAddInstantBidInfo({ data: instantBidPayload }));
      changeInstantBidview(true);
    } catch (error) {
      console.error('Auto-submit error:', error);
      toastFn('error', 'Failed to submit bid. Please try again.', addBidToastId);
    }
  }, [cognitiveSearchResults, relevantCommodities, allCommoditydata, bidtypes, bidwriters, memberId, instantBidFile, fileName, reqDocIds, bidLegalAds, dispatch, changeInstantBidview]);

  useEffect(() => {
    const fetchData = async () => {
      // const jobID = Number(job);
      if (jobId !== 0 && !Number.isNaN(jobId)) {
        // setJobId(jobID);
        // setStatus('pending');
        // setProgress(28);
        const res = await getJobStatus(jobId);
        setStatus(res.status);
        setFileName(res.filename);
      }
    };
    fetchData();
  }, [jobId]);

  useEffect(() => {
    return () => {
      dispatch(setAddBidDetails({ ResetState: true }));
    };
  }, [dispatch]);
  const isProcessing = ['uploading', 'pending', 'processing'].includes(status);

  useEffect(() => {
    if (jobId === 0) return;

    let finalProgressInterval: any = null;

    const interval = setInterval(async () => {
      try {
        const res = await getJobStatus(jobId);
        setStatus(res.status);
        if(!fileName){
          setFileName(res.filename);
        }
        if (res.status === 'processing') {
          setProgress(prev => (prev < 50 ? 50 : prev));
          if (res.processed_questions === res.total_questions) {
            setProgress(prev => (prev < 75 ? 75 : prev));
            if (!finalProgressInterval) {
              finalProgressInterval = setInterval(() => {
                setProgress(prev => {
                  if (prev >= 96) {
                    clearInterval(finalProgressInterval);
                    finalProgressInterval = null;
                    return prev;
                  }
                  return prev + Math.random() * 2;
                });
              }, 1300);
            }
          }
        }

        if (res.status === 'completed') {
          if (finalProgressInterval) {
            clearInterval(finalProgressInterval);
            finalProgressInterval = null;
          }
          setProgress(100);
          const response = await getJobResult(jobId);
          const final = transformInstantBidData(response?.extracted_data);
          setReqDocIds(final.reqDocIds);
          setSearchQuery(getCommodityQueryValue(final?.commodities, final?.relatedtags));
          setFormData(final);
          setShowData(true);
          dispatch(setAddBidDetails({ instantBidData: final, showInstantBid: true }));
          autoSubmitTriggeredRef.current = false;
          setHasAutoSubmitted(true);
          clearInterval(interval);
        }

        if (res.status === 'failed') {
          history.goBack();
          if (finalProgressInterval) clearInterval(finalProgressInterval);
          resetFile();
          setError(
            'Job failed. Please re-upload the file and try again. If the issue persists, contact support with the Bid Document.',
          );
          clearInterval(interval);
        }
      } catch (err: any) {
        resetFile();
        setError(err?.message);
        clearInterval(interval);
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      if (finalProgressInterval) clearInterval(finalProgressInterval);
    };
  }, [jobId,dispatch, fileName]);

  useEffect(() => {
    let interval: any;

    if (status === 'pending') {
      setProgress(25);
      interval = setInterval(() => {
        setProgress(prev => (prev >= 45 ? prev : prev + Math.random() * 2));
      }, 1300);
    }
    if (status === 'processing') {
      setProgress(prev => (prev < 50 ? 50 : prev));
      interval = setInterval(() => {
        setProgress(prev => (prev >= 71 ? prev : prev + Math.random() * 2));
      }, 4000);
    }
    if (status === 'completed') setProgress(100);

    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (!editBidId) {
      dispatch(setAddBidDetails({ ResetState: true }));
    }
  }, [dispatch, editBidId]);


  useEffect(() => {
    if (!hasAutoSubmitted || autoSubmitTriggeredRef.current) {
      return;
    }

    if (!formData.bidNumber) {
      return;
    }

    const hasCommodities = Boolean(cognitiveSearchResults?.value?.length) || relevantCommodities.length > 0;
    if (!hasCommodities) {
      return;
    }

    autoSubmitTriggeredRef.current = true;
    handleAutoSubmit(formData);
  }, [allCommoditydata, cognitiveSearchResults, formData, handleAutoSubmit, hasAutoSubmitted, relevantCommodities.length]);

  useEffect(() => {
    if (bidwriters.length === 0) dispatch(getAddBidBidWriters({}));
    if (bidtypes.length === 0) dispatch(getAddBidBidTypes({}));
    dispatch(getSavedLegal());
  }, [bidtypes.length, bidwriters.length, dispatch]);
  return (
    <>
      <div className='container'>
        <div
          className='card min-vh-100 p-4'
          onDrop={e => {
            e.preventDefault();
            setIsDragging(false);
            validateAndSetFile(e.dataTransfer.files);
          }}
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
        >
          <h2 className='arrowWrapper mr-auto mt-4'>Add Instant Bid with OpenBids AI</h2>
          <p className='upload-description'>
            Simply upload your bid specification document and let our AI instantly analyze it. Key
            bid details will be automatically extracted and populated into the form, helping you
            create bids faster, smarter, and with greater accuracy.
          </p>

          <div className='mt-4'>
            <FileUploadBox
              fileName={fileName}
              isDragging={isDragging}
              fileError={fileError}
              fileInputRef={fileInputRef}
              onDragOver={e => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => {
                e.preventDefault();
                setIsDragging(false);
                validateAndSetFile(e.dataTransfer.files);
              }}
              onFileChange={validateAndSetFile}
              onReset={resetFile}
            />

            <UploadProgressBar status={status} progress={progress} uploadProgress={uploadProgress} />

            {status === 'completed' && (
              <div className='text-center my-4'>
                <span className='mr-2 text-success mdi mdi-check-bold' />
                Successfully Extracted Bid Details!
                <small> Analysis Completed,please wait while we create your bid</small>
              </div>
            )}
{/* {status === 'uploading' && (
  <div className="text-center mt-2 text-muted small">
    Uploading... {uploadProgress}%
  </div>
)} */}
            {!showData && !isProcessing && (
              <div className='text-right mt-3'>
                <DSButton onClick={handleSubmit} inactive={!fileName}>
                  Upload &amp; Extract
                </DSButton>
              </div>
            )}

            {error && <DSAlert type={Status.Error}>{error}</DSAlert>}
          </div>
        </div>
      </div>
      <ModalPopUp title='Duplicate Bid' closeModal={false} isOpen={duplicateInstantBidModel}>
        <DuplicateInstantBid />
      </ModalPopUp>
    </>
  );
}
